import { Link } from "react-router-dom";
import { Trophy, Flag, Camera, Users, BarChart3, Sparkles, ArrowRight } from "lucide-react";

const FEATURES = [
  {
    icon: Flag,
    title: "Rondas inteligentes",
    desc: "Captura score hoyo por hoyo con handicap, apuestas y ganadores calculados en tiempo real. GCC, Match Play, Nassau, Skins, Units y Oyeses en una sola app.",
  },
  {
    icon: Trophy,
    title: "Competencias y rankings",
    desc: "Tu club organiza torneos con categorías por género, edad, handicap y bolas. Los rankings se actualizan solos al terminar cada ronda.",
  },
  {
    icon: Camera,
    title: "CADii Stories",
    desc: "Comparte tus mejores momentos con overlays de score, Shot Tracer animado sincronizado a tu video, mascotas y filtros nativos.",
  },
  {
    icon: Users,
    title: "Cadiihouse (Web)",
    desc: "Panel completo para clubes: gestionar jugadores, categorías, torneos, tee-times y rankings desde el navegador.",
  },
  {
    icon: BarChart3,
    title: "Historial y estadísticas",
    desc: "Cada ronda que juegas queda guardada con su tarjeta completa y su tablero live. Comparte cualquiera con un tap.",
  },
  {
    icon: Sparkles,
    title: "Diseñada para golfistas",
    desc: "Sin publicidad. Sin apuestas de dinero real. Solo golf, marcador y comunidad.",
  },
];

export default function Marketing() {
  return (
    <div className="min-h-screen bg-[#F4F1EC]" data-testid="marketing-page">
      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 pt-12 pb-16">
        <Link to="/" className="text-[#1B4D3E] font-semibold text-sm hover:underline" data-testid="marketing-home-link">
          ← Volver
        </Link>

        <div className="mt-8 flex flex-col md:flex-row items-start md:items-center gap-6">
          <img
            src="/cadii/brand/logo_c_circle.png"
            alt="CADii"
            className="w-16 h-16 object-contain shrink-0"
          />
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#102E32] leading-[1.05]">
              El marcador digital <span className="text-[#1B4D3E]">para golfistas</span>
            </h1>
            <p className="text-lg text-[#475569] mt-4 max-w-2xl leading-relaxed">
              CADii es la app para registrar rondas, competir con y sin apuestas amistosas, y
              compartir tus mejores tiros. Diseñada por golfistas, para golfistas.
            </p>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href="#features"
            className="inline-flex items-center gap-2 rounded-full bg-[#1B4D3E] hover:bg-[#163F33] text-white px-6 py-3 text-sm font-bold transition-colors"
            data-testid="marketing-cta-features"
          >
            Ver funciones <ArrowRight className="w-4 h-4" />
          </a>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5E7EB] hover:border-[#1B4D3E] text-[#102E32] px-6 py-3 text-sm font-bold transition-colors"
            data-testid="marketing-cta-support"
          >
            Centro de soporte
          </Link>
        </div>
      </div>

      {/* Features grid */}
      <div id="features" className="bg-white border-y border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-6 py-16">
          <h2 className="text-2xl sm:text-3xl font-black text-[#102E32] mb-2">Todo lo que necesitas en el campo</h2>
          <p className="text-[#475569] mb-10">Una app que entiende cómo juega y compite el golfista de hoy.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FEATURES.map((f, idx) => {
              const Icon = f.icon;
              return (
                <div
                  key={idx}
                  className="rounded-2xl border border-[#E5E7EB] p-6 hover:border-[#1B4D3E]/50 hover:shadow-sm transition-all"
                  data-testid={`marketing-feature-${idx}`}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#1B4D3E]/10 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#1B4D3E]" />
                  </div>
                  <h3 className="text-lg font-black text-[#102E32] mb-1.5">{f.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Who it's for */}
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="rounded-3xl bg-[#102E32] text-white p-8 sm:p-12" data-testid="marketing-audience">
          <h2 className="text-2xl sm:text-3xl font-black mb-3">Para golfistas, clubes y comunidades</h2>
          <p className="text-white/80 max-w-2xl leading-relaxed">
            Ya sea que juegues los sábados con tus amigos o organices torneos formales para tu club,
            CADii tiene un flujo para ti. Los jugadores usan la app móvil; los clubes gestionan todo
            desde <strong className="text-[#E8C76A]">Cadiihouse</strong>, el panel web.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <p className="text-3xl font-black text-[#E8C76A]">Jugadores</p>
              <p className="text-sm text-white/70 mt-1">Rondas, apuestas, historia y Stories.</p>
            </div>
            <div>
              <p className="text-3xl font-black text-[#E8C76A]">Clubes</p>
              <p className="text-sm text-white/70 mt-1">Torneos, categorías, rankings y equipo.</p>
            </div>
            <div>
              <p className="text-3xl font-black text-[#E8C76A]">Comunidad</p>
              <p className="text-sm text-white/70 mt-1">Comparte tableros y compite entre clubes.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer / legal */}
      <div className="max-w-5xl mx-auto px-6 pb-12">
        <div className="flex flex-wrap gap-4 text-sm text-[#64748B]">
          <Link to="/support" className="hover:text-[#1B4D3E] hover:underline" data-testid="marketing-support-link">Soporte</Link>
          <span>·</span>
          <Link to="/privacy" className="hover:text-[#1B4D3E] hover:underline" data-testid="marketing-privacy-link">Privacidad</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-[#1B4D3E] hover:underline" data-testid="marketing-terms-link">Términos</Link>
        </div>
        <p className="mt-6 text-xs text-[#94A3B8]">© {new Date().getFullYear()} CADii · Golf, competencias y comunidad.</p>
      </div>
    </div>
  );
}
